const statusDe = {
  ELIGIBLE: 'Mahnfähig',
  PREPARED: 'Vorbereitet',
  PROPOSED: 'Vorschlag',
  APPROVED: 'Freigegeben',
  GENERATED: 'PDF erzeugt',
  SENT: 'Versendet',
  COMPLETED: 'Abgeschlossen',
  CANCELLED: 'Storniert',
  COLLECTION: 'Inkasso',
} as const;
const eventDe = {
  PREPARED: 'Vorschlag erstellt',
  APPROVED: 'Freigegeben',
  PDF_GENERATED: 'PDF revisionssicher abgelegt',
  SENT: 'Versendet',
  CANCELLED: 'Storniert',
  COLLECTION: 'An Inkasso übergeben',
  ASSIGNED: 'Verantwortung geändert',
} as const;

export const dunningDe = {
  title: 'Mahnwesen',
  subtitle: 'Offene Posten kontrolliert prüfen, freigeben und nachweisbar versenden.',
  controlled: {
    title: 'Kein automatischer Versand',
    description:
      'Ein Mahnlauf erstellt ausschließlich Vorschläge. Freigabe und Versand erfolgen getrennt und nur durch berechtigte Personen.',
  },
  tabs: {
    open: 'Fällige & überfällige Posten',
    runs: 'Mahnläufe & Vorschläge',
    sent: 'Versendete Mahnungen',
    customerHistory: 'Kundenhistorie',
    excluded: 'Gesperrte Vorgänge',
    settings: 'Einstellungen',
  },
  kpi: {
    overdue: 'Überfällige Posten',
    amount: 'Überfälliger Betrag',
    proposals: 'Offene Vorschläge',
    sent: 'Versendet',
  },
  table: {
    invoice: 'Rechnung',
    customer: 'Kunde',
    dueDate: 'Fällig am',
    openAmount: 'Offener Betrag',
    level: 'Stufe',
    status: 'Status',
    principal: 'Hauptforderung',
    fees: 'Gebühren',
    interest: 'Zinsen',
    total: 'Gesamtbetrag',
  },
  status: statusDe,
  event: eventDe,
  exclusion: {
    CUSTOMER_BLOCK: 'Kundensperre',
    RISK_STATUS: 'Risiko-/Inkassostatus',
    DISPUTED: 'Strittig',
    DOCUMENT_BLOCK: 'Mahnsperre',
    SETTLED_BY_CREDIT: 'Durch Gutschrift ausgeglichen',
  },
  states: {
    error: 'Mahnwesen konnte nicht geladen werden',
    noOpenItems: 'Keine passenden offenen Posten vorhanden.',
    noRuns: 'Noch keine Mahnläufe vorhanden.',
    noSent: 'Noch keine Mahnungen versendet.',
  },
  actions: {
    prepare: 'Mahnlauf vorbereiten',
    approve: 'Vorschau freigeben',
    send: 'Jetzt versenden',
    pdf: 'PDF öffnen',
    block: 'Mahnposten sperren',
    unblock: 'Sperre aufheben',
    dispute: 'Als strittig markieren',
    recordPayment: 'Zahlung erfassen',
    cancelRun: 'Lauf verwerfen',
    collection: 'An Inkasso übergeben',
  },
  settings: {
    grace: 'Kulanzfrist in Tagen',
    minimum: 'Mindestbetrag',
    interest: 'Standardzins p. a. in %',
    followUp: 'Wiedervorlage in Tagen',
    combine: 'Rechnungen je Kunde bündeln',
    autoPrepare: 'Regelmäßig Vorschläge vorbereiten',
    stages: 'Mahnstufen',
    level: 'Stufe',
    days: 'Tage nach Fälligkeit',
    fee: 'Gebühr',
  },
  preview: {
    title: 'Unverbindliche Vorschau',
    description:
      'Vor der Freigabe werden Zahlungen, Gutschriften und Sperren erneut geprüft. Die Freigabe friert den fachlichen Snapshot ein und erzeugt die PDFs.',
  },
  approveDialog: {
    title: 'Mahnlauf verbindlich freigeben?',
    description:
      'Die Mahnnummern, Beträge und offenen Posten werden unveränderlich gespeichert. Es wird noch keine E-Mail versendet.',
  },
  sendDialog: {
    title: 'Mahnung ausdrücklich versenden?',
    description:
      'Die freigegebene Mahnung wird mit ihrem unveränderlichen PDF an {{email}} versendet.',
  },
  prepared: 'Mahnvorschlag erstellt.',
  approved: 'Mahnlauf freigegeben und PDFs abgelegt.',
  sent: 'Mahnung versendet.',
  cancelled: 'Mahnlauf verworfen.',
  saved: 'Änderungen gespeichert.',
  collectionTransferred: 'Inkassoübergabe protokolliert.',
  proposal: 'Mahnvorschlag',
  level: 'Mahnstufe {{level}}',
  runSummary: '{{notices}} Mahnungen · {{items}} offene Posten',
  daysOverdue: '{{count}} Tage überfällig',
  manualBlockReason: 'Manuelle Mahnsperre im Mahnwesen',
  manualDisputeReason: 'Forderung im Mahnwesen als strittig markiert',
  noEmail: 'keine E-Mail-Adresse',
  collectionReference: 'Inkasso-Aktenzeichen',
  cancelReason: 'Begründung für das Verwerfen',
  history: 'Status- und Versandhistorie',
  paymentDialog: {
    title: 'Zahlungseingang erfassen',
    description: 'Zahlung für Rechnung {{invoice}} vollständig einem OPOS zuordnen.',
    amount: 'Zahlbetrag',
    reference: 'Bankreferenz',
  },
  paymentRecorded: 'Zahlung verbucht.',
  payments: 'Zahlungsverknüpfungen',
  noReference: 'ohne Referenz',
} as const;

export const dunningEn = {
  title: 'Dunning management',
  subtitle: 'Review, approve and dispatch open-item reminders with full evidence.',
  controlled: {
    title: 'No automatic dispatch',
    description:
      'A dunning run only creates proposals. Approval and dispatch are separate actions restricted to authorised users.',
  },
  tabs: {
    open: 'Due & overdue items',
    runs: 'Runs & proposals',
    sent: 'Sent reminders',
    customerHistory: 'Customer history',
    excluded: 'Excluded items',
    settings: 'Settings',
  },
  kpi: {
    overdue: 'Overdue items',
    amount: 'Overdue amount',
    proposals: 'Open proposals',
    sent: 'Sent',
  },
  table: {
    invoice: 'Invoice',
    customer: 'Customer',
    dueDate: 'Due date',
    openAmount: 'Outstanding',
    level: 'Level',
    status: 'Status',
    principal: 'Principal',
    fees: 'Fees',
    interest: 'Interest',
    total: 'Total due',
  },
  status: {
    ELIGIBLE: 'Eligible',
    PREPARED: 'Prepared',
    PROPOSED: 'Proposal',
    APPROVED: 'Approved',
    GENERATED: 'PDF generated',
    SENT: 'Sent',
    COMPLETED: 'Completed',
    CANCELLED: 'Cancelled',
    COLLECTION: 'Collection',
  },
  event: {
    PREPARED: 'Proposal prepared',
    APPROVED: 'Approved',
    PDF_GENERATED: 'PDF archived',
    SENT: 'Sent',
    CANCELLED: 'Cancelled',
    COLLECTION: 'Transferred to collection',
    ASSIGNED: 'Responsibility changed',
  },
  exclusion: {
    CUSTOMER_BLOCK: 'Customer block',
    RISK_STATUS: 'Risk/collection status',
    DISPUTED: 'Disputed',
    DOCUMENT_BLOCK: 'Dunning block',
    SETTLED_BY_CREDIT: 'Settled by credit note',
  },
  states: {
    error: 'Dunning data could not be loaded',
    noOpenItems: 'No matching open items.',
    noRuns: 'No dunning runs yet.',
    noSent: 'No reminders sent yet.',
  },
  actions: {
    prepare: 'Prepare dunning run',
    approve: 'Approve preview',
    send: 'Send now',
    pdf: 'Open PDF',
    block: 'Block item',
    unblock: 'Remove block',
    dispute: 'Mark disputed',
    recordPayment: 'Record payment',
    cancelRun: 'Discard run',
    collection: 'Transfer to collection',
  },
  settings: {
    grace: 'Grace period in days',
    minimum: 'Minimum amount',
    interest: 'Default annual interest %',
    followUp: 'Follow-up in days',
    combine: 'Combine invoices per customer',
    autoPrepare: 'Prepare proposals regularly',
    stages: 'Dunning levels',
    level: 'Level',
    days: 'Days after due date',
    fee: 'Fee',
  },
  preview: {
    title: 'Non-binding preview',
    description:
      'Payments, credits and blocks are checked again before approval. Approval freezes the business snapshot and generates the PDFs.',
  },
  approveDialog: {
    title: 'Approve this dunning run?',
    description: 'Reminder numbers, amounts and open items become immutable. No email is sent yet.',
  },
  sendDialog: {
    title: 'Explicitly send reminder?',
    description: 'The approved reminder and its immutable PDF will be sent to {{email}}.',
  },
  prepared: 'Dunning proposal prepared.',
  approved: 'Run approved and PDFs archived.',
  sent: 'Reminder sent.',
  cancelled: 'Run discarded.',
  saved: 'Changes saved.',
  collectionTransferred: 'Collection transfer recorded.',
  proposal: 'Reminder proposal',
  level: 'Dunning level {{level}}',
  runSummary: '{{notices}} reminders · {{items}} open items',
  daysOverdue: '{{count}} days overdue',
  manualBlockReason: 'Manual block in dunning management',
  manualDisputeReason: 'Receivable marked as disputed in dunning management',
  noEmail: 'no email address',
  collectionReference: 'Collection reference',
  cancelReason: 'Reason for discarding',
  history: 'Status and dispatch history',
  paymentDialog: {
    title: 'Record incoming payment',
    description: 'Allocate the payment for invoice {{invoice}} to its open item.',
    amount: 'Payment amount',
    reference: 'Bank reference',
  },
  paymentRecorded: 'Payment posted.',
  payments: 'Linked payments',
  noReference: 'no reference',
} as const;

export const dunningTr = {
  ...dunningEn,
  title: 'Ödeme hatırlatma yönetimi',
  subtitle: 'Açık kalemleri kontrollü biçimde inceleyin, onaylayın ve kanıtlı olarak gönderin.',
  controlled: {
    title: 'Otomatik gönderim yok',
    description:
      'Bir çalıştırma yalnızca öneri oluşturur. Onay ve gönderim ayrı ve yetkiye bağlı işlemlerdir.',
  },
  tabs: {
    open: 'Vadesi gelen/geçen kalemler',
    runs: 'Çalıştırmalar ve öneriler',
    sent: 'Gönderilen hatırlatmalar',
    customerHistory: 'Müşteri geçmişi',
    excluded: 'Hariç tutulan işlemler',
    settings: 'Ayarlar',
  },
  actions: {
    prepare: 'Hatırlatma çalıştırmasını hazırla',
    approve: 'Önizlemeyi onayla',
    send: 'Şimdi gönder',
    pdf: 'PDF’yi aç',
    block: 'Kalemi engelle',
    unblock: 'Engeli kaldır',
    dispute: 'İhtilaflı işaretle',
    recordPayment: 'Ödeme kaydet',
    cancelRun: 'Çalıştırmayı iptal et',
    collection: 'Tahsilata devret',
  },
  status: {
    ELIGIBLE: 'Uygun',
    PREPARED: 'Hazırlandı',
    PROPOSED: 'Öneri',
    APPROVED: 'Onaylandı',
    GENERATED: 'PDF oluşturuldu',
    SENT: 'Gönderildi',
    COMPLETED: 'Tamamlandı',
    CANCELLED: 'İptal edildi',
    COLLECTION: 'Tahsilat',
  },
  kpi: {
    overdue: 'Vadesi geçen kalemler',
    amount: 'Vadesi geçen tutar',
    proposals: 'Açık öneriler',
    sent: 'Gönderildi',
  },
  table: {
    invoice: 'Fatura',
    customer: 'Müşteri',
    dueDate: 'Vade tarihi',
    openAmount: 'Açık tutar',
    level: 'Seviye',
    status: 'Durum',
    principal: 'Ana alacak',
    fees: 'Ücretler',
    interest: 'Faiz',
    total: 'Toplam tutar',
  },
  event: {
    PREPARED: 'Öneri hazırlandı',
    APPROVED: 'Onaylandı',
    PDF_GENERATED: 'PDF arşivlendi',
    SENT: 'Gönderildi',
    CANCELLED: 'İptal edildi',
    COLLECTION: 'Tahsilata devredildi',
    ASSIGNED: 'Sorumluluk değiştirildi',
  },
  exclusion: {
    CUSTOMER_BLOCK: 'Müşteri engeli',
    RISK_STATUS: 'Risk/tahsilat durumu',
    DISPUTED: 'İhtilaflı',
    DOCUMENT_BLOCK: 'Hatırlatma engeli',
    SETTLED_BY_CREDIT: 'Alacak dekontuyla kapandı',
  },
  states: {
    error: 'Hatırlatma verileri yüklenemedi',
    noOpenItems: 'Uygun açık kalem yok.',
    noRuns: 'Henüz çalıştırma yok.',
    noSent: 'Henüz hatırlatma gönderilmedi.',
  },
  settings: {
    grace: 'Ek süre (gün)',
    minimum: 'Asgari tutar',
    interest: 'Varsayılan yıllık faiz %',
    followUp: 'Takip süresi (gün)',
    combine: 'Müşteri faturalarını birleştir',
    autoPrepare: 'Düzenli öneri hazırla',
    stages: 'Hatırlatma seviyeleri',
    level: 'Seviye',
    days: 'Vade sonrası gün',
    fee: 'Ücret',
  },
  preview: {
    title: 'Bağlayıcı olmayan önizleme',
    description:
      'Onaydan önce ödemeler, alacaklar ve engeller yeniden kontrol edilir. Onay, ticari anlık görüntüyü dondurur ve PDF’leri oluşturur.',
  },
  approveDialog: {
    title: 'Bu çalıştırma onaylansın mı?',
    description:
      'Numaralar, tutarlar ve açık kalemler değiştirilemez biçimde saklanır. Henüz e-posta gönderilmez.',
  },
  sendDialog: {
    title: 'Hatırlatma açıkça gönderilsin mi?',
    description: 'Onaylı hatırlatma ve değiştirilemez PDF’si {{email}} adresine gönderilir.',
  },
  prepared: 'Hatırlatma önerisi hazırlandı.',
  approved: 'Çalıştırma onaylandı ve PDF’ler arşivlendi.',
  sent: 'Hatırlatma gönderildi.',
  cancelled: 'Çalıştırma iptal edildi.',
  saved: 'Değişiklikler kaydedildi.',
  collectionTransferred: 'Tahsilat devri kaydedildi.',
  proposal: 'Hatırlatma önerisi',
  level: 'Hatırlatma seviyesi {{level}}',
  runSummary: '{{notices}} hatırlatma · {{items}} açık kalem',
  daysOverdue: '{{count}} gün gecikmiş',
  manualBlockReason: 'Hatırlatma yönetiminde manuel engel',
  manualDisputeReason: 'Alacak ihtilaflı olarak işaretlendi',
  noEmail: 'e-posta adresi yok',
  collectionReference: 'Tahsilat dosya numarası',
  cancelReason: 'İptal gerekçesi',
  history: 'Durum ve gönderim geçmişi',
  paymentDialog: {
    title: 'Gelen ödemeyi kaydet',
    description: '{{invoice}} faturası ödemesini açık kaleme tahsis edin.',
    amount: 'Ödeme tutarı',
    reference: 'Banka referansı',
  },
  paymentRecorded: 'Ödeme işlendi.',
  payments: 'Bağlı ödemeler',
  noReference: 'referans yok',
} as const;

export const dunningFr = {
  ...dunningEn,
  title: 'Gestion des relances',
  subtitle: 'Contrôler, valider et envoyer les relances avec une preuve complète.',
  controlled: {
    title: 'Aucun envoi automatique',
    description:
      'Un cycle crée uniquement des propositions. La validation et l’envoi sont des actions distinctes réservées aux personnes autorisées.',
  },
  tabs: {
    open: 'Postes échus',
    runs: 'Cycles et propositions',
    sent: 'Relances envoyées',
    customerHistory: 'Historique client',
    excluded: 'Postes exclus',
    settings: 'Paramètres',
  },
  actions: {
    prepare: 'Préparer un cycle',
    approve: 'Valider l’aperçu',
    send: 'Envoyer maintenant',
    pdf: 'Ouvrir le PDF',
    block: 'Bloquer le poste',
    unblock: 'Lever le blocage',
    dispute: 'Marquer comme contesté',
    recordPayment: 'Enregistrer un paiement',
    cancelRun: 'Abandonner le cycle',
    collection: 'Transmettre au recouvrement',
  },
  status: {
    ELIGIBLE: 'Éligible',
    PREPARED: 'Préparé',
    PROPOSED: 'Proposition',
    APPROVED: 'Validé',
    GENERATED: 'PDF généré',
    SENT: 'Envoyé',
    COMPLETED: 'Terminé',
    CANCELLED: 'Annulé',
    COLLECTION: 'Recouvrement',
  },
  kpi: {
    overdue: 'Postes en retard',
    amount: 'Montant en retard',
    proposals: 'Propositions ouvertes',
    sent: 'Envoyées',
  },
  table: {
    invoice: 'Facture',
    customer: 'Client',
    dueDate: 'Échéance',
    openAmount: 'Solde ouvert',
    level: 'Niveau',
    status: 'Statut',
    principal: 'Principal',
    fees: 'Frais',
    interest: 'Intérêts',
    total: 'Total dû',
  },
  event: {
    PREPARED: 'Proposition préparée',
    APPROVED: 'Validée',
    PDF_GENERATED: 'PDF archivé',
    SENT: 'Envoyée',
    CANCELLED: 'Annulée',
    COLLECTION: 'Transmise au recouvrement',
    ASSIGNED: 'Responsabilité modifiée',
  },
  exclusion: {
    CUSTOMER_BLOCK: 'Blocage client',
    RISK_STATUS: 'Statut risque/recouvrement',
    DISPUTED: 'Contesté',
    DOCUMENT_BLOCK: 'Blocage de relance',
    SETTLED_BY_CREDIT: 'Soldé par avoir',
  },
  states: {
    error: 'Impossible de charger les relances',
    noOpenItems: 'Aucun poste ouvert correspondant.',
    noRuns: 'Aucun cycle de relance.',
    noSent: 'Aucune relance envoyée.',
  },
  settings: {
    grace: 'Délai de grâce en jours',
    minimum: 'Montant minimal',
    interest: 'Intérêt annuel par défaut %',
    followUp: 'Rappel interne en jours',
    combine: 'Regrouper les factures par client',
    autoPrepare: 'Préparer régulièrement des propositions',
    stages: 'Niveaux de relance',
    level: 'Niveau',
    days: 'Jours après échéance',
    fee: 'Frais',
  },
  preview: {
    title: 'Aperçu non contraignant',
    description:
      'Paiements, avoirs et blocages sont revérifiés avant validation. La validation fige l’instantané métier et génère les PDF.',
  },
  approveDialog: {
    title: 'Valider ce cycle de relance ?',
    description:
      'Les numéros, montants et postes ouverts deviennent immuables. Aucun courriel n’est encore envoyé.',
  },
  sendDialog: {
    title: 'Envoyer explicitement la relance ?',
    description: 'La relance validée et son PDF immuable seront envoyés à {{email}}.',
  },
  prepared: 'Proposition de relance préparée.',
  approved: 'Cycle validé et PDF archivés.',
  sent: 'Relance envoyée.',
  cancelled: 'Cycle abandonné.',
  saved: 'Modifications enregistrées.',
  collectionTransferred: 'Transmission au recouvrement enregistrée.',
  proposal: 'Proposition de relance',
  level: 'Niveau de relance {{level}}',
  runSummary: '{{notices}} relances · {{items}} postes ouverts',
  daysOverdue: '{{count}} jours de retard',
  manualBlockReason: 'Blocage manuel dans la gestion des relances',
  manualDisputeReason: 'Créance marquée comme contestée',
  noEmail: 'aucune adresse e-mail',
  collectionReference: 'Référence de recouvrement',
  cancelReason: 'Motif de l’abandon',
  history: 'Historique des statuts et envois',
  paymentDialog: {
    title: 'Enregistrer le paiement reçu',
    description: 'Affecter le paiement de la facture {{invoice}} au poste ouvert.',
    amount: 'Montant payé',
    reference: 'Référence bancaire',
  },
  paymentRecorded: 'Paiement comptabilisé.',
  payments: 'Paiements liés',
  noReference: 'sans référence',
} as const;

export const dunningEs = {
  ...dunningEn,
  title: 'Gestión de cobros',
  subtitle: 'Revise, apruebe y envíe recordatorios con evidencia completa.',
  controlled: {
    title: 'Sin envío automático',
    description:
      'Una ejecución solo crea propuestas. La aprobación y el envío son acciones separadas y restringidas.',
  },
  tabs: {
    open: 'Partidas vencidas',
    runs: 'Ejecuciones y propuestas',
    sent: 'Recordatorios enviados',
    customerHistory: 'Historial del cliente',
    excluded: 'Partidas excluidas',
    settings: 'Configuración',
  },
  actions: {
    prepare: 'Preparar ejecución',
    approve: 'Aprobar vista previa',
    send: 'Enviar ahora',
    pdf: 'Abrir PDF',
    block: 'Bloquear partida',
    unblock: 'Quitar bloqueo',
    dispute: 'Marcar en disputa',
    recordPayment: 'Registrar pago',
    cancelRun: 'Descartar ejecución',
    collection: 'Transferir a recobro',
  },
  status: {
    ELIGIBLE: 'Apta',
    PREPARED: 'Preparada',
    PROPOSED: 'Propuesta',
    APPROVED: 'Aprobada',
    GENERATED: 'PDF generado',
    SENT: 'Enviada',
    COMPLETED: 'Completada',
    CANCELLED: 'Cancelada',
    COLLECTION: 'Recobro',
  },
  kpi: {
    overdue: 'Partidas vencidas',
    amount: 'Importe vencido',
    proposals: 'Propuestas abiertas',
    sent: 'Enviadas',
  },
  table: {
    invoice: 'Factura',
    customer: 'Cliente',
    dueDate: 'Vencimiento',
    openAmount: 'Importe pendiente',
    level: 'Nivel',
    status: 'Estado',
    principal: 'Principal',
    fees: 'Gastos',
    interest: 'Intereses',
    total: 'Total',
  },
  event: {
    PREPARED: 'Propuesta preparada',
    APPROVED: 'Aprobada',
    PDF_GENERATED: 'PDF archivado',
    SENT: 'Enviada',
    CANCELLED: 'Cancelada',
    COLLECTION: 'Transferida a recobro',
    ASSIGNED: 'Responsabilidad modificada',
  },
  exclusion: {
    CUSTOMER_BLOCK: 'Bloqueo del cliente',
    RISK_STATUS: 'Estado de riesgo/recobro',
    DISPUTED: 'En disputa',
    DOCUMENT_BLOCK: 'Bloqueo del aviso',
    SETTLED_BY_CREDIT: 'Saldada con abono',
  },
  states: {
    error: 'No se pudieron cargar los avisos',
    noOpenItems: 'No hay partidas abiertas coincidentes.',
    noRuns: 'Todavía no hay ejecuciones.',
    noSent: 'Todavía no hay avisos enviados.',
  },
  settings: {
    grace: 'Periodo de gracia en días',
    minimum: 'Importe mínimo',
    interest: 'Interés anual predeterminado %',
    followUp: 'Seguimiento en días',
    combine: 'Agrupar facturas por cliente',
    autoPrepare: 'Preparar propuestas periódicamente',
    stages: 'Niveles de aviso',
    level: 'Nivel',
    days: 'Días tras vencimiento',
    fee: 'Gasto',
  },
  preview: {
    title: 'Vista previa no vinculante',
    description:
      'Los pagos, abonos y bloqueos se comprueban de nuevo antes de aprobar. La aprobación congela la instantánea y genera los PDF.',
  },
  approveDialog: {
    title: '¿Aprobar esta ejecución?',
    description:
      'Los números, importes y partidas quedan inmutables. Todavía no se envía ningún correo.',
  },
  sendDialog: {
    title: '¿Enviar expresamente el aviso?',
    description: 'El aviso aprobado y su PDF inmutable se enviarán a {{email}}.',
  },
  prepared: 'Propuesta preparada.',
  approved: 'Ejecución aprobada y PDF archivados.',
  sent: 'Aviso enviado.',
  cancelled: 'Ejecución descartada.',
  saved: 'Cambios guardados.',
  collectionTransferred: 'Transferencia a recobro registrada.',
  proposal: 'Propuesta de aviso',
  level: 'Nivel de aviso {{level}}',
  runSummary: '{{notices}} avisos · {{items}} partidas abiertas',
  daysOverdue: '{{count}} días de retraso',
  manualBlockReason: 'Bloqueo manual en gestión de cobros',
  manualDisputeReason: 'Crédito marcado en disputa',
  noEmail: 'sin dirección de correo',
  collectionReference: 'Referencia de recobro',
  cancelReason: 'Motivo del descarte',
  history: 'Historial de estados y envíos',
  paymentDialog: {
    title: 'Registrar pago recibido',
    description: 'Asigne el pago de la factura {{invoice}} a su partida abierta.',
    amount: 'Importe pagado',
    reference: 'Referencia bancaria',
  },
  paymentRecorded: 'Pago contabilizado.',
  payments: 'Pagos vinculados',
  noReference: 'sin referencia',
} as const;

export const dunningRu = {
  ...dunningEn,
  title: 'Управление задолженностью',
  subtitle: 'Проверяйте, утверждайте и отправляйте напоминания с полным подтверждением.',
  controlled: {
    title: 'Без автоматической отправки',
    description:
      'Запуск создаёт только предложения. Утверждение и отправка выполняются отдельно уполномоченными сотрудниками.',
  },
  tabs: {
    open: 'Просроченные позиции',
    runs: 'Запуски и предложения',
    sent: 'Отправленные напоминания',
    customerHistory: 'История клиента',
    excluded: 'Исключённые позиции',
    settings: 'Настройки',
  },
  actions: {
    prepare: 'Подготовить запуск',
    approve: 'Утвердить просмотр',
    send: 'Отправить',
    pdf: 'Открыть PDF',
    block: 'Заблокировать',
    unblock: 'Снять блокировку',
    dispute: 'Отметить спорной',
    recordPayment: 'Зарегистрировать платёж',
    cancelRun: 'Отменить запуск',
    collection: 'Передать на взыскание',
  },
  status: {
    ELIGIBLE: 'Доступно',
    PREPARED: 'Подготовлено',
    PROPOSED: 'Предложение',
    APPROVED: 'Утверждено',
    GENERATED: 'PDF создан',
    SENT: 'Отправлено',
    COMPLETED: 'Завершено',
    CANCELLED: 'Отменено',
    COLLECTION: 'Взыскание',
  },
  kpi: {
    overdue: 'Просроченные позиции',
    amount: 'Просроченная сумма',
    proposals: 'Открытые предложения',
    sent: 'Отправлено',
  },
  table: {
    invoice: 'Счёт',
    customer: 'Клиент',
    dueDate: 'Срок оплаты',
    openAmount: 'Остаток',
    level: 'Уровень',
    status: 'Статус',
    principal: 'Основной долг',
    fees: 'Сборы',
    interest: 'Проценты',
    total: 'Итого',
  },
  event: {
    PREPARED: 'Предложение подготовлено',
    APPROVED: 'Утверждено',
    PDF_GENERATED: 'PDF сохранён',
    SENT: 'Отправлено',
    CANCELLED: 'Отменено',
    COLLECTION: 'Передано на взыскание',
    ASSIGNED: 'Ответственный изменён',
  },
  exclusion: {
    CUSTOMER_BLOCK: 'Блокировка клиента',
    RISK_STATUS: 'Риск/взыскание',
    DISPUTED: 'Спорная',
    DOCUMENT_BLOCK: 'Блокировка напоминания',
    SETTLED_BY_CREDIT: 'Закрыто кредит-нотой',
  },
  states: {
    error: 'Не удалось загрузить задолженность',
    noOpenItems: 'Подходящих открытых позиций нет.',
    noRuns: 'Запусков пока нет.',
    noSent: 'Напоминания пока не отправлялись.',
  },
  settings: {
    grace: 'Льготный срок в днях',
    minimum: 'Минимальная сумма',
    interest: 'Годовая ставка по умолчанию %',
    followUp: 'Повторная проверка через, дней',
    combine: 'Объединять счета клиента',
    autoPrepare: 'Регулярно готовить предложения',
    stages: 'Уровни напоминаний',
    level: 'Уровень',
    days: 'Дней после срока',
    fee: 'Сбор',
  },
  preview: {
    title: 'Необязательный просмотр',
    description:
      'Перед утверждением платежи, кредит-ноты и блокировки проверяются повторно. Утверждение фиксирует снимок и создаёт PDF.',
  },
  approveDialog: {
    title: 'Утвердить этот запуск?',
    description:
      'Номера, суммы и открытые позиции станут неизменяемыми. Письма ещё не отправляются.',
  },
  sendDialog: {
    title: 'Явно отправить напоминание?',
    description: 'Утверждённое напоминание и его неизменяемый PDF будут отправлены на {{email}}.',
  },
  prepared: 'Предложение подготовлено.',
  approved: 'Запуск утверждён, PDF сохранены.',
  sent: 'Напоминание отправлено.',
  cancelled: 'Запуск отменён.',
  saved: 'Изменения сохранены.',
  collectionTransferred: 'Передача на взыскание зарегистрирована.',
  proposal: 'Предложение напоминания',
  level: 'Уровень {{level}}',
  runSummary: '{{notices}} напоминаний · {{items}} открытых позиций',
  daysOverdue: 'Просрочка: {{count}} дней',
  manualBlockReason: 'Ручная блокировка в управлении задолженностью',
  manualDisputeReason: 'Требование отмечено как спорное',
  noEmail: 'нет адреса эл. почты',
  collectionReference: 'Номер дела взыскания',
  cancelReason: 'Причина отмены',
  history: 'История статусов и отправок',
  paymentDialog: {
    title: 'Зарегистрировать поступление',
    description: 'Распределите платёж по счёту {{invoice}} на открытую позицию.',
    amount: 'Сумма платежа',
    reference: 'Банковская ссылка',
  },
  paymentRecorded: 'Платёж проведён.',
  payments: 'Связанные платежи',
  noReference: 'без ссылки',
} as const;
